import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ModalHeader from './ui/ModalHeader';
import { useModalDrag } from '../hooks/useModalDrag';

interface ScreenshotEditorProps {
  isOpen: boolean;
  imageData: string;
  onClose: () => void;
  onApply: (editedImageData: string) => void;
}

const ScreenshotEditor: React.FC<ScreenshotEditorProps> = ({ isOpen, imageData, onClose, onApply }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [penColor, setPenColor] = useState<'red' | 'blue' | 'green'>('red');
  const [hasEdits, setHasEdits] = useState(false);
  const [tool, setTool] = useState<'pen' | 'arrow' | 'circle'>('pen');
  const [arrowColor, setArrowColor] = useState<'red' | 'blue' | 'green'>('red');
  const [circleColor, setCircleColor] = useState<'red' | 'blue' | 'green'>('red');
  const [circleSize, setCircleSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [penThickness, setPenThickness] = useState<'thin' | 'medium' | 'thick'>('medium');
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  // Initialize canvas when image loads
  useEffect(() => {
    if (!isOpen || !imageData) return;

    const image = new Image();
    image.src = imageData;
    image.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas size to match image
      canvas.width = image.width;
      canvas.height = image.height;

      // Draw the image
      ctx.drawImage(image, 0, 0);
    };
  }, [isOpen, imageData]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool === 'arrow') {
      // Handle arrow tool click
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;

      drawArrow(x, y);
    } else if (tool === 'circle') {
      // Handle circle tool click
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;

      drawCircle(x, y);
    } else {
      setIsDrawing(true);
      draw(e);
    }
  };

  const drawArrow = (x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const colorMap = {
      red: '#FF0000',
      blue: '#0066FF',
      green: '#00CC00'
    };

    // Save the current state
    ctx.save();

    // Translate to the click position
    ctx.translate(x, y);

    // Rotate for downward-right arrow
    ctx.rotate((45 * Math.PI) / 180); // 45 degrees

    // Scale the arrow (adjust size as needed)
    const scale = 2;
    ctx.scale(scale, scale);

    // Center the arrow around the origin (arrow viewBox is 24x24, so center is at 12, 12)
    ctx.translate(-12, -12);

    // Draw the arrow path
    ctx.fillStyle = colorMap[arrowColor];
    const path = new Path2D('m13.595,23.999c-.229,0-.455-.021-.674-.061-1.467-.267-2.947-1.463-2.947-3.532v-6.394H3.595C1.524,14.013.328,12.532.061,11.064c-.267-1.467.331-3.273,2.268-4.003L18.723.268c1.473-.555,3.047-.216,4.136.874,1.091,1.09,1.43,2.663.886,4.105l-6.83,16.457c-.611,1.621-2.011,2.295-3.319,2.295Z');
    ctx.fill(path);

    // Restore the context
    ctx.restore();

    setHasEdits(true);
  };

  const drawCircle = (x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const colorMap = {
      red: '#FF0000',
      blue: '#0066FF',
      green: '#00CC00'
    };

    const sizeMap = {
      small: 80,
      medium: 160,
      large: 320
    };

    const radius = sizeMap[circleSize];

    // Draw circle outline
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = colorMap[circleColor];
    ctx.lineWidth = 4;
    ctx.stroke();

    setHasEdits(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Update cursor position for circle and arrow preview
    if (tool === 'circle' || tool === 'arrow') {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      setCursorPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }

    // Continue with drawing if pen tool is active
    if (tool === 'pen') {
      draw(e);
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing && e.type !== 'mousedown') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set pen color
    const colorMap = {
      red: '#FF0000',
      blue: '#0066FF',
      green: '#00CC00'
    };

    const thicknessMap = {
      thin: 2,
      medium: 5,
      thick: 10
    };

    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = colorMap[penColor];
    ctx.lineWidth = thicknessMap[penThickness];
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    if (e.type === 'mousedown') {
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
      ctx.stroke();
      // Start a new path from the current position to prevent overlapping
      ctx.beginPath();
      ctx.moveTo(x, y);
    }

    setHasEdits(true);
  };

  const clearEdits = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const image = new Image();
    image.src = imageData;
    image.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0);
      setHasEdits(false);
    };
  };

  const handleApply = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const editedImageData = canvas.toDataURL('image/png');
    onApply(editedImageData);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-scrim bg-opacity-90 p-4"
      style={{ zIndex: 2147483647 }}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative w-[90vw] h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader title="Edit Screenshot" onClose={onClose} dragHandleProps={dragHandleProps} />
        {/* Tool palette */}
        <div className="flex items-center justify-between mb-4 bg-gray-800 rounded-b-lg p-4">
          <div className="flex items-center space-x-4">
            {/* Tool Selection */}
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setTool('pen');
                }}
                className={`p-3 rounded-md transition-colors ${
                  tool === 'pen'
                    ? 'bg-blue-600'
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
                title="Pen Tool"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="white">
                  <path d="M1.172,19.119A4,4,0,0,0,0,21.947V24H2.053a4,4,0,0,0,2.828-1.172L18.224,9.485,14.515,5.776Z"/>
                  <path d="M23.145.855a2.622,2.622,0,0,0-3.71,0L15.929,4.362l3.709,3.709,3.507-3.506A2.622,2.622,0,0,0,23.145.855Z"/>
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setTool('arrow');
                }}
                className={`p-3 rounded-md transition-colors ${
                  tool === 'arrow'
                    ? 'bg-blue-600'
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
                title="Arrow Tool"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="white">
                  <path d="m13.595,23.999c-.229,0-.455-.021-.674-.061-1.467-.267-2.947-1.463-2.947-3.532v-6.394H3.595C1.524,14.013.328,12.532.061,11.064c-.267-1.467.331-3.273,2.268-4.003L18.723.268c1.473-.555,3.047-.216,4.136.874,1.091,1.09,1.43,2.663.886,4.105l-6.83,16.457c-.611,1.621-2.011,2.295-3.319,2.295Z"/>
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setTool('circle');
                }}
                className={`p-3 rounded-md transition-colors ${
                  tool === 'circle'
                    ? 'bg-blue-600'
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
                title="Circle Tool"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                </svg>
              </button>
            </div>

            {/* Pen Color Selection (only shown for pen tool) */}
            {tool === 'pen' && (
              <>
                <div className="flex items-center space-x-2 border-l border-gray-600 pl-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPenColor('red');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      penColor === 'red'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Red Pen"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="#FF0000">
                      <path d="M1.172,19.119A4,4,0,0,0,0,21.947V24H2.053a4,4,0,0,0,2.828-1.172L18.224,9.485,14.515,5.776Z"/>
                      <path d="M23.145.855a2.622,2.622,0,0,0-3.71,0L15.929,4.362l3.709,3.709,3.507-3.506A2.622,2.622,0,0,0,23.145.855Z"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPenColor('blue');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      penColor === 'blue'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Blue Pen"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="#0066FF">
                      <path d="M1.172,19.119A4,4,0,0,0,0,21.947V24H2.053a4,4,0,0,0,2.828-1.172L18.224,9.485,14.515,5.776Z"/>
                      <path d="M23.145.855a2.622,2.622,0,0,0-3.71,0L15.929,4.362l3.709,3.709,3.507-3.506A2.622,2.622,0,0,0,23.145.855Z"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPenColor('green');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      penColor === 'green'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Green Pen"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="#00CC00">
                      <path d="M1.172,19.119A4,4,0,0,0,0,21.947V24H2.053a4,4,0,0,0,2.828-1.172L18.224,9.485,14.515,5.776Z"/>
                      <path d="M23.145.855a2.622,2.622,0,0,0-3.71,0L15.929,4.362l3.709,3.709,3.507-3.506A2.622,2.622,0,0,0,23.145.855Z"/>
                    </svg>
                  </button>
                </div>

                {/* Pen Thickness Selection */}
                <div className="flex items-center space-x-2 border-l border-gray-600 pl-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPenThickness('thin');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      penThickness === 'thin'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Thin"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5">
                      <line x1="2" y1="12" x2="22" y2="12" stroke="white" strokeWidth="1" strokeLinecap="round"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPenThickness('medium');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      penThickness === 'medium'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Medium"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5">
                      <line x1="2" y1="12" x2="22" y2="12" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPenThickness('thick');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      penThickness === 'thick'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Thick"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5">
                      <line x1="2" y1="12" x2="22" y2="12" stroke="white" strokeWidth="5" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
              </>
            )}

            {/* Arrow Color Selection (only shown for arrow tool) */}
            {tool === 'arrow' && (
              <div className="flex items-center space-x-2 border-l border-gray-600 pl-4">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setArrowColor('red');
                  }}
                  className={`p-3 rounded-md transition-colors ${
                    arrowColor === 'red'
                      ? 'bg-gray-600'
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  title="Red Arrow"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="#FF0000">
                    <path d="m13.595,23.999c-.229,0-.455-.021-.674-.061-1.467-.267-2.947-1.463-2.947-3.532v-6.394H3.595C1.524,14.013.328,12.532.061,11.064c-.267-1.467.331-3.273,2.268-4.003L18.723.268c1.473-.555,3.047-.216,4.136.874,1.091,1.09,1.43,2.663.886,4.105l-6.83,16.457c-.611,1.621-2.011,2.295-3.319,2.295Z"/>
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setArrowColor('blue');
                  }}
                  className={`p-3 rounded-md transition-colors ${
                    arrowColor === 'blue'
                      ? 'bg-gray-600'
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  title="Blue Arrow"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="#0066FF">
                    <path d="m13.595,23.999c-.229,0-.455-.021-.674-.061-1.467-.267-2.947-1.463-2.947-3.532v-6.394H3.595C1.524,14.013.328,12.532.061,11.064c-.267-1.467.331-3.273,2.268-4.003L18.723.268c1.473-.555,3.047-.216,4.136.874,1.091,1.09,1.43,2.663.886,4.105l-6.83,16.457c-.611,1.621-2.011,2.295-3.319,2.295Z"/>
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setArrowColor('green');
                  }}
                  className={`p-3 rounded-md transition-colors ${
                    arrowColor === 'green'
                      ? 'bg-gray-600'
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  title="Green Arrow"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="#00CC00">
                    <path d="m13.595,23.999c-.229,0-.455-.021-.674-.061-1.467-.267-2.947-1.463-2.947-3.532v-6.394H3.595C1.524,14.013.328,12.532.061,11.064c-.267-1.467.331-3.273,2.268-4.003L18.723.268c1.473-.555,3.047-.216,4.136.874,1.091,1.09,1.43,2.663.886,4.105l-6.83,16.457c-.611,1.621-2.011,2.295-3.319,2.295Z"/>
                  </svg>
                </button>
              </div>
            )}

            {/* Circle Color Selection (only shown for circle tool) */}
            {tool === 'circle' && (
              <>
                <div className="flex items-center space-x-2 border-l border-gray-600 pl-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCircleColor('red');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      circleColor === 'red'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Red Circle"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#FF0000" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCircleColor('blue');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      circleColor === 'blue'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Blue Circle"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#0066FF" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCircleColor('green');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      circleColor === 'green'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Green Circle"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#00CC00" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  </button>
                </div>

                {/* Circle Size Selection */}
                <div className="flex items-center space-x-2 border-l border-gray-600 pl-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCircleSize('small');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      circleSize === 'small'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Small Circle"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="white" strokeWidth="2">
                      <circle cx="12" cy="12" r="5"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCircleSize('medium');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      circleSize === 'medium'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Medium Circle"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="white" strokeWidth="2">
                      <circle cx="12" cy="12" r="8"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCircleSize('large');
                    }}
                    className={`p-3 rounded-md transition-colors ${
                      circleSize === 'large'
                        ? 'bg-gray-600'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title="Large Circle"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="white" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearEdits();
              }}
              disabled={!hasEdits}
              className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleApply();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 flex items-center justify-center overflow-auto">
          <div className="relative" style={{ maxWidth: '80vw', maxHeight: '80vh' }}>
            <canvas
              ref={canvasRef}
              onMouseDown={startDrawing}
              onMouseMove={handleMouseMove}
              onMouseUp={stopDrawing}
              onMouseLeave={() => {
                stopDrawing();
                setCursorPosition(null);
              }}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              style={{
                display: 'block',
                cursor: tool === 'arrow' || tool === 'circle' ? 'none' : 'crosshair'
              }}
            />

            {/* Arrow Preview */}
            {tool === 'arrow' && cursorPosition && canvasRef.current && (
              <svg
                style={{
                  position: 'absolute',
                  left: cursorPosition.x,
                  top: cursorPosition.y,
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                  pointerEvents: 'none',
                  width: `${48 * (canvasRef.current.offsetWidth / canvasRef.current.width)}px`,
                  height: `${48 * (canvasRef.current.offsetHeight / canvasRef.current.height)}px`,
                }}
                viewBox="0 0 24 24"
              >
                <path
                  d="m13.595,23.999c-.229,0-.455-.021-.674-.061-1.467-.267-2.947-1.463-2.947-3.532v-6.394H3.595C1.524,14.013.328,12.532.061,11.064c-.267-1.467.331-3.273,2.268-4.003L18.723.268c1.473-.555,3.047-.216,4.136.874,1.091,1.09,1.43,2.663.886,4.105l-6.83,16.457c-.611,1.621-2.011,2.295-3.319,2.295Z"
                  fill={arrowColor === 'red' ? 'rgba(255, 0, 0, 0.2)' : arrowColor === 'blue' ? 'rgba(0, 102, 255, 0.2)' : 'rgba(0, 204, 0, 0.2)'}
                />
              </svg>
            )}

            {/* Circle Preview */}
            {tool === 'circle' && cursorPosition && canvasRef.current && (
              <div
                style={{
                  position: 'absolute',
                  left: cursorPosition.x,
                  top: cursorPosition.y,
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  width: `${(circleSize === 'small' ? 80 : circleSize === 'medium' ? 160 : 320) * 2 * (canvasRef.current.offsetWidth / canvasRef.current.width)}px`,
                  height: `${(circleSize === 'small' ? 80 : circleSize === 'medium' ? 160 : 320) * 2 * (canvasRef.current.offsetHeight / canvasRef.current.height)}px`,
                  borderRadius: '50%',
                  border: `${4 * (canvasRef.current.offsetWidth / canvasRef.current.width)}px solid ${circleColor === 'red' ? 'rgba(255, 0, 0, 0.2)' : circleColor === 'blue' ? 'rgba(0, 102, 255, 0.2)' : 'rgba(0, 204, 0, 0.2)'}`,
                }}
              />
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-4 text-center text-gray-400 text-sm">
          {tool === 'pen'
            ? 'Click and drag to draw on the screenshot. Select a pen color to annotate the image.'
            : tool === 'arrow'
            ? 'Click on the screenshot to place an arrow. Select an arrow color to annotate the image.'
            : 'Click on the screenshot to place a circle. Select a color and size for the circle.'}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ScreenshotEditor;
